import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import { sendBookingNotification, deleteBookingNotification, editBookingNotification } from './telegram-notifications';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Отсутствуют ключи Supabase. Пожалуйста, проверьте переменные окружения.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        storageKey: 'franema-rental-auth',
        storage: {
            getItem: key => localStorage.getItem(key),
            setItem: (key, value) => localStorage.setItem(key, value),
            removeItem: key => localStorage.removeItem(key),
        }
    }
});

// Supabase клиент с сервисной ролью для админских операций
export const supabaseServiceRole = createClient<Database>(
    supabaseUrl,
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9wemtvZnlzdWRkenFyeHJyZnJ5Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0MjM5OTg4OSwiZXhwIjoyMDU3OTc1ODg5fQ.krTwDo3Oda4G5LrcqVlXhIFJJYSqcMaXuipnW0LArJs',
    {
        auth: {
            persistSession: false,
            autoRefreshToken: false,
            storageKey: 'franema-rental-service-auth',
            storage: {
                getItem: key => localStorage.getItem(key),
                setItem: (key, value) => localStorage.setItem(key, value),
                removeItem: key => localStorage.removeItem(key),
            }
        }
    }
);

// Сервисный ключ для Edge функций
// Используем константу, так как сервисный ключ встроен в frontend и не является секретом
// в контексте Edge функций с Supabase
const EDGE_FUNCTION_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9wemtvZnlzdWRkenFyeHJyZnJ5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIzOTk4ODksImV4cCI6MjA1Nzk3NTg4OX0.GSIckb042pHvc_RcmNLPT39FXA-o1HrEsBUt9UIhHA8';

/*
// Заголовки для запросов к Edge функциям
const getEdgeFunctionHeaders = () => {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${EDGE_FUNCTION_KEY}`
    };
};
*/

/* 
 * Временно отключено использование функций Edge из-за проблем с авторизацией
 * Вместо этого используется прямой доступ к таблицам
 *
// Получение JWT токена для запросов к функциям
const getAuthToken = async () => {
    const session = await supabase.auth.getSession();
    return session?.data?.session?.access_token;
};

// Получение заголовков для запросов к Edge-функциям
const getEdgeFunctionHeaders = async () => {
    const token = await getAuthToken();
    return {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
};
*/

// Хелперы для типизированной работы с базой данных
export const getEquipment = async (categoryId?: string) => {
    let query = supabaseServiceRole.from('equipment').select('*');

    if (categoryId) {
        query = query.eq('category_id', categoryId);
    }

    return await query.order('sort_order', { ascending: true }).order('name');
};

export const getEquipmentById = async (id: string) => {
    return await supabaseServiceRole.from('equipment').select('*').eq('id', id).single();
};

export const getCategories = async () => {
    try {
        console.log('Получение списка категорий (прямой доступ)');
        
        // Прямой доступ к таблице категорий с сервисной ролью
        const { data, error } = await supabaseServiceRole
            .from('categories')
            .select('*')
            .order('sort_order', { ascending: true });
            
        if (error) {
            console.error('Ошибка получения категорий (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при получении категорий:', error);
        return { data: null, error };
    }
};

// Обновленная версия getUserBookings для страницы профиля

// Старая функция getBookings, поддерживаем для обратной совместимости
export const getBookings = async (userId?: string) => {
    const query = supabase.from('bookings').select(`
    *,
    equipment (*)
  `);

    if (userId) {
        query.eq('user_id', userId);
    }

    return query;
};

export const getUserBookings = async (userId: string) => {
    try {
        console.log('Получение бронирований пользователя с ID:', userId);
        
        // Выполняем запрос напрямую через supabaseServiceRole для обхода ограничений прав доступа
        const { data: bookings, error: bookingsError } = await supabaseServiceRole
            .from('bookings')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });
            
        if (bookingsError) {
            console.error('Ошибка при получении бронирований:', bookingsError);
            return { data: [], error: bookingsError };
        }
        
        console.log('Результаты запроса бронирований пользователя:', bookings);
        
        if (!bookings || bookings.length === 0) {
            console.log('Бронирования не найдены для пользователя:', userId);
            return { data: [], error: null };
        }
        
        console.log(`Найдено ${bookings.length} бронирований для пользователя ${userId}`);
        
        // Для каждого бронирования получаем информацию о оборудовании
        const bookingsWithEquipment = await Promise.all(bookings.map(async (booking) => {
            console.log(`Получение оборудования для бронирования ${booking.id}`);
            
            // Получаем записи из booking_equipment для данного бронирования
            const { data: bookingEquipment, error: equipmentError } = await supabaseServiceRole
                .from('booking_equipment')
                .select('*, equipment(*)')
                .eq('booking_id', booking.id);
                
            if (equipmentError) {
                console.error(`Ошибка при получении оборудования для бронирования ${booking.id}:`, equipmentError);
                return {
                    ...booking,
                    equipment: []
                };
            }
            
            console.log(`Получено ${bookingEquipment?.length || 0} записей оборудования для бронирования ${booking.id}:`, bookingEquipment);
            
            // Извлекаем информацию о оборудовании и добавляем к каждому элементу количество из booking_equipment
            const equipment = bookingEquipment?.map(item => ({
                ...item.equipment,
                quantity: item.quantity
            })) || [];
            
            return {
                ...booking,
                equipment
            };
        }));
        
        console.log('Итоговые данные о бронированиях с оборудованием:', bookingsWithEquipment);
        return { data: bookingsWithEquipment, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при получении бронирований:', error);
        return { data: [], error };
    }
};

// Обновленный метод для авторизации через Telegram
export const loginWithTelegram = async (telegramData: any) => {
    try {
        console.log('Начало авторизации через Telegram', { 
            id: telegramData.id, 
            first_name: telegramData.first_name 
        });

        // Проверяем наличие необходимых данных
        if (!telegramData || !telegramData.id || !telegramData.hash) {
            console.error('Неполные данные от Telegram:', telegramData);
            return {
                data: null,
                error: 'Получены неполные данные от Telegram'
            };
        }

        // Вызываем Edge-функцию для верификации данных от Telegram
        console.log('Отправка запроса на verify-telegram');
        const verifyResponse = await fetch(`${supabaseUrl}/functions/v1/verify-telegram`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${EDGE_FUNCTION_KEY}`
            },
            body: JSON.stringify(telegramData),
        });

        if (!verifyResponse.ok) {
            const errorText = await verifyResponse.text();
            console.error('Ошибка верификации данных Telegram:', {
                status: verifyResponse.status,
                statusText: verifyResponse.statusText,
                body: errorText
            });
            throw new Error(`Ошибка верификации данных Telegram: ${verifyResponse.status} ${errorText}`);
        }

        const verifyResult = await verifyResponse.json();
        console.log('Результат верификации:', verifyResult);

        if (!verifyResult.success) {
            console.error('Неуспешная верификация:', verifyResult.error);
            throw new Error(verifyResult.error || 'Неверные данные авторизации Telegram');
        }

        // Теперь создаем или обновляем пользователя в базе данных
        const userData = verifyResult.data;
        console.log('Отправка запроса на создание/обновление пользователя:', userData);
        
        const userResponse = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${EDGE_FUNCTION_KEY}`
            },
            body: JSON.stringify({
                action: 'createOrUpdate',
                userData,
            }),
        });

        if (!userResponse.ok) {
            const errorText = await userResponse.text();
            console.error('Ошибка создания/обновления пользователя:', {
                status: userResponse.status,
                statusText: userResponse.statusText,
                body: errorText
            });
            throw new Error(`Ошибка создания/обновления пользователя: ${userResponse.status} ${errorText}`);
        }

        const userResult = await userResponse.json();
        console.log('Результат создания/обновления пользователя:', userResult);
        
        return {
            data: userResult.user,
            error: null,
        };
    } catch (error) {
        console.error('Ошибка при авторизации через Telegram:', error);
        return {
            data: null,
            error: error instanceof Error ? error.message : 'Неизвестная ошибка',
        };
    }
};

// Тип для данных при создании бронирования
export interface CreateBookingData {
    user_id: string;
    start_date: string;
    end_date: string;
    total_price: number;
    guests: number;
    equipment_items?: {
        equipment_id: string;
        quantity: number;
        daily_rate: number;
    }[];
    equipment_data?: string; // JSON строка с данными об оборудовании
    project_name?: string | null; // Новое поле - название проекта
}

// Создание нового бронирования
export const createBooking = async (bookingData: CreateBookingData) => {
    console.log('Создание бронирования:', bookingData);
    
    try {
        let equipmentItems = bookingData.equipment_items;
        
        // Если equipment_data предоставлены в виде строки JSON, распарсим их
        if (bookingData.equipment_data && !equipmentItems) {
            try {
                equipmentItems = JSON.parse(bookingData.equipment_data);
            } catch (e) {
                console.error('Ошибка при парсинге equipment_data:', e);
                return { error: 'Некорректный формат данных оборудования' };
            }
        }
        
        // Проверка наличия данных об оборудовании
        if (!equipmentItems || equipmentItems.length === 0) {
            return { error: 'Не выбрано оборудование для бронирования' };
        }

        // Начинаем транзакцию
        const { data: bookingResult, error: bookingError } = await supabaseServiceRole
            .from('bookings')
            .insert({
                user_id: bookingData.user_id,
                start_date: bookingData.start_date,
                end_date: bookingData.end_date,
                total_price: bookingData.total_price,
                guests: bookingData.guests,
                project_name: bookingData.project_name,
                status: 'pending' // По умолчанию статус "в ожидании"
            })
            .select()
            .single();

        if (bookingError) {
            console.error('Ошибка при создании бронирования:', bookingError);
            return { data: null, error: bookingError };
        }
        
        if (!bookingResult) {
            return { data: null, error: { message: 'Не удалось создать запись о бронировании' } };
        }
        
        const booking_id = bookingResult.id;
        console.log('Создано бронирование с ID:', booking_id);
        
        // Создаем массив записей для booking_equipment
        const bookingEquipmentData = equipmentItems.map(item => ({
            booking_id,
            equipment_id: item.equipment_id,
            quantity: item.quantity
        }));

        // Вставляем все записи booking_equipment одним запросом
        const { error: equipmentError } = await supabaseServiceRole
            .from('booking_equipment')
            .insert(bookingEquipmentData);
        
        if (equipmentError) {
            console.error('Ошибка при добавлении оборудования к бронированию:', equipmentError);
            
            // Если произошла ошибка, удаляем созданное бронирование
            await supabaseServiceRole
                .from('bookings')
                .delete()
                .eq('id', booking_id);
            
            return { data: null, error: equipmentError };
        }
        
        // Отправляем уведомление в Telegram
        try {
            const notificationSent = await sendBookingNotification(booking_id);
            console.log(`Уведомление о бронировании ${booking_id} ${notificationSent ? 'успешно отправлено' : 'не удалось отправить'} в Telegram`);
        } catch (notificationError) {
            // Не прерываем процесс создания бронирования при ошибке отправки уведомления
            console.error('Ошибка при отправке уведомления о бронировании:', notificationError);
        }
        
        return { data: { booking_id, success: true }, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при создании бронирования:', error);
        return { data: null, error: { message: 'Непредвиденная ошибка при создании бронирования' } };
    }
};

// Функции для работы с оборудованием - прямой доступ
export const createEquipment = async (equipmentData: Database['public']['Tables']['equipment']['Insert']) => {
    try {
        console.log('Создание оборудования (прямой доступ):', equipmentData);
        
        // Прямой доступ к таблице оборудования
        const { data, error } = await supabaseServiceRole
            .from('equipment')
            .insert(equipmentData)
            .select();
            
        if (error) {
            console.error('Ошибка создания оборудования (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при создании оборудования:', error);
        return { data: null, error };
    }
};

export const updateEquipment = async (id: string, equipmentData: Database['public']['Tables']['equipment']['Update']) => {
    try {
        console.log('Обновление оборудования (прямой доступ):', id, equipmentData);
        
        // Прямой доступ к таблице оборудования
        const { data, error } = await supabaseServiceRole
            .from('equipment')
            .update(equipmentData)
            .eq('id', id)
            .select();
            
        if (error) {
            console.error('Ошибка обновления оборудования (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при обновлении оборудования:', error);
        return { data: null, error };
    }
};

export const deleteEquipment = async (id: string) => {
    try {
        console.log('Удаление оборудования (прямой доступ):', id);
        
        // Прямой доступ к таблице оборудования
        const { data, error } = await supabaseServiceRole
            .from('equipment')
            .delete()
            .eq('id', id)
            .select();
            
        if (error) {
            console.error('Ошибка удаления оборудования (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при удалении оборудования:', error);
        return { data: null, error };
    }
};

// Обновленный метод для загрузки изображений с использованием Storage API напрямую
export const uploadEquipmentImage = async (file: File, path: string) => {
    try {
        console.log('Загрузка изображения (прямой доступ):', { path });
        
        // Преобразуем File в Blob для совместимости
        const blob = new Blob([await file.arrayBuffer()], { type: file.type });
        
        // Используем Storage API напрямую с сервисной ролью
        const { data, error } = await supabaseServiceRole
            .storage
            .from('equipment-images') // Используем созданный бакет equipment-images
            .upload(path, blob, {
                cacheControl: '3600',
                upsert: true,
                contentType: file.type
            });
            
        if (error) {
            console.error('Ошибка загрузки файла (прямой доступ):', error);
            throw error;
        }
        
        // Получаем публичный URL для загруженного файла
        const { data: publicUrlData } = supabaseServiceRole
            .storage
            .from('equipment-images') // Используем тот же бакет
            .getPublicUrl(data.path);
            
        return publicUrlData.publicUrl;
    } catch (error) {
        console.error('Ошибка при загрузке изображения:', error);
        throw error;
    }
};

export const deleteEquipmentImage = async (path: string) => {
    try {
        console.log('Удаление изображения (прямой доступ):', { path });
        
        // Используем Storage API напрямую с сервисной ролью
        const { error } = await supabaseServiceRole
            .storage
            .from('equipment-images') // Используем тот же бакет
            .remove([path]);
            
        if (error) {
            console.error('Ошибка удаления файла (прямой доступ):', error);
            throw error;
        }
        
        return true;
    } catch (error) {
        console.error('Ошибка при удалении изображения:', error);
        throw error;
    }
};

export const listEquipmentImages = async (path: string = '') => {
    try {
        console.log('Получение списка изображений (прямой доступ):', { path });
        
        // Используем Storage API напрямую с сервисной ролью
        const { data, error } = await supabaseServiceRole
            .storage
            .from('equipment-images') // Используем тот же бакет
            .list(path);
            
        if (error) {
            console.error('Ошибка получения списка файлов (прямой доступ):', error);
            throw error;
        }
        
        return data;
    } catch (error) {
        console.error('Ошибка при получении списка изображений:', error);
        throw error;
    }
};

// Обновленные функции для работы с категориями через прямой доступ
export const createCategory = async (categoryData: Database['public']['Tables']['categories']['Insert']) => {
    try {
        console.log('Создание категории (прямой доступ):', categoryData);
        
        // Прямой доступ к таблице категорий
        const { data, error } = await supabase
            .from('categories')
            .insert(categoryData)
            .select();
            
        if (error) {
            console.error('Ошибка создания категории (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при создании категории:', error);
        return { data: null, error };
    }
};

export const updateCategory = async (id: string, categoryData: Database['public']['Tables']['categories']['Update']) => {
    try {
        console.log('Обновление категории (прямой доступ):', id, categoryData);
        
        // Прямой доступ к таблице категорий
        const { data, error } = await supabase
            .from('categories')
            .update(categoryData)
            .eq('id', id)
            .select();
            
        if (error) {
            console.error('Ошибка обновления категории (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при обновлении категории:', error);
        return { data: null, error };
    }
};

export const deleteCategory = async (id: string) => {
    try {
        console.log('Удаление категории:', id);
        
        // Сначала проверяем, используется ли категория в оборудовании
        const { data: equipmentCheck, error: checkError } = await supabaseServiceRole
            .from('equipment')
            .select('id')
            .eq('category_id', id)
            .limit(1);
            
        if (checkError) {
            console.error('Ошибка при проверке использования категории:', checkError);
            return { data: null, error: checkError };
        }
        
        if (equipmentCheck && equipmentCheck.length > 0) {
            const error = new Error('Категория используется в оборудовании и не может быть удалена');
            return { data: null, error };
        }
        
        // Прямой доступ к таблице категорий
        const { data, error } = await supabaseServiceRole
            .from('categories')
            .delete()
            .eq('id', id)
            .select();
            
        if (error) {
            console.error('Ошибка при удалении категории (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при удалении категории:', error);
        return { data: null, error };
    }
};

export const getCategoryById = async (id: string) => {
    try {
        console.log('Запрос категории по ID (прямой доступ):', id);
        
        // Прямой доступ к таблице категорий
        const { data, error } = await supabase
            .from('categories')
            .select('*')
            .eq('id', id)
            .single();
            
        if (error) {
            console.error('Ошибка при получении категории (прямой доступ):', error);
            return { data: null, error };
        }
        
        return { data, error: null };
    } catch (error) {
        console.error('Ошибка при получении категории:', error);
        return { data: null, error };
    }
};

// Типы статусов оборудования
export type EquipmentStatus = 'available' | 'booked' | 'maintenance';

// Функция для обновления статуса оборудования
export const updateEquipmentStatus = async (equipmentId: string, status: EquipmentStatus) => {
  const { data, error } = await supabase
    .from('equipment')
    .update({ status })
    .eq('id', equipmentId)
    .select();

  return { data, error };
};

// Функция для проверки доступности оборудования на заданные даты
export const checkEquipmentAvailability = async (startDate: Date | null, endDate: Date | null) => {
  try {
    // Если даты не указаны, возвращаем все оборудование
    if (!startDate || !endDate) {
      console.log('Даты не указаны, получаем все оборудование');
      
      const { data, error } = await supabaseServiceRole
        .from('equipment')
        .select('*')
        .neq('status', 'deleted')
        .order('sort_order', { ascending: true })
        .order('name');
      
      if (error) {
        console.error('Ошибка при получении списка оборудования с бронированиями:', error);
        
        // Запасной вариант: получаем все оборудование обычным способом
        const { data: allEquipment, error: equipmentError } = await supabaseServiceRole
          .from('equipment')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('name');
        
        if (equipmentError) {
          console.error('Ошибка при получении списка оборудования:', equipmentError);
          return { data: [], error: equipmentError };
        }
        
        // Добавляем информацию о доступности для всего оборудования
        const equipmentWithAvailability = allEquipment.map((item: any) => ({
          ...item,
          is_available: true,
          available_quantity: item.quantity,
          booked_dates: []
        }));
        
        return { data: equipmentWithAvailability, error: null };
      }
      
      // Вместо проверки is_available, предоставляем информацию о бронированиях
      console.log(`Получено оборудования: ${data.length}`);
      
      return { data, error: null };
    }
    
    // Подготавливаем даты с правильными временными значениями
    const formattedStartDate = new Date(startDate);
    const formattedEndDate = new Date(endDate);
    
    // Устанавливаем время 00:00:00 для даты начала
    formattedStartDate.setHours(0, 0, 0, 0);
    
    // Устанавливаем время 23:59:59 для даты конца
    formattedEndDate.setHours(23, 59, 59, 999);
    
    // Форматируем даты для запроса
    const formattedStartIso = formattedStartDate.toISOString();
    const formattedEndIso = formattedEndDate.toISOString();
    
    console.log(`Проверка доступности на период: ${formattedStartIso} - ${formattedEndIso}`);
    
    // Используем обновленную SQL-функцию для проверки доступности
    try {
      const { data, error } = await supabaseServiceRole.rpc(
        'check_equipment_availability',
        {
          p_start_date: formattedStartIso,
          p_end_date: formattedEndIso
        }
      );
      
      if (error) {
        console.error('Ошибка при проверке доступности оборудования:', error);
        throw error;
      }
      
      // Проверяем структуру данных
      if (!Array.isArray(data)) {
        console.warn('Неожиданный формат данных от функции: данные не являются массивом');
        throw new Error('Неверный формат данных');
      }
      
      // Логирование для отладки
      if (data.length > 0) {
        console.log('Пример данных:', JSON.stringify(data[0]));
      }
      
      console.log(`Доступно оборудования: ${data.filter(item => item.is_available).length} из ${data.length}`);
      
      // Сортируем результаты по sort_order
      const sortedData = [...data].sort((a, b) => {
        // Используем приведение типов для доступа к свойству sort_order
        const aAny = a as any;
        const bAny = b as any;
        
        // Если у обоих элементов есть sort_order
        if (aAny.sort_order !== null && aAny.sort_order !== undefined && 
            bAny.sort_order !== null && bAny.sort_order !== undefined) {
          return aAny.sort_order - bAny.sort_order;
        }
        // Если только у a есть sort_order
        if (aAny.sort_order !== null && aAny.sort_order !== undefined) {
          return -1;
        }
        // Если только у b есть sort_order
        if (bAny.sort_order !== null && bAny.sort_order !== undefined) {
          return 1;
        }
        // Если ни у одного нет sort_order, сортируем по имени
        return a.name.localeCompare(b.name);
      });
      
      return { data: sortedData, error: null };
    } catch (functionError) {
      console.error('Переход на запасной вариант из-за ошибки:', functionError);
      
      // Запасной вариант: получаем все оборудование, если функция не работает
      const { data: allEquipment, error: equipmentError } = await supabaseServiceRole
        .from('equipment')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name');
      
      if (equipmentError) {
        console.error('Ошибка при получении списка оборудования:', equipmentError);
        return { data: [], error: equipmentError };
      }
      
      // Возвращаем все оборудование как доступное
      return { 
        data: allEquipment.map((item: any) => ({ 
          ...item, 
          is_available: true, 
          available_quantity: item.quantity,
          booked_dates: []
        })), 
        error: null 
      };
    }
  } catch (error) {
    console.error('Ошибка при проверке доступности оборудования:', error);
    return { data: [], error };
  }
};

// Получение всех бронирований с расширенными данными
export const getAllBookings = async () => {
  console.log('Вызов функции getAllBookings...');
  try {
    // Получаем все бронирования без вложенного запроса users
    const { data, error } = await supabaseServiceRole
      .from('bookings')
      .select(`
        *,
        booking_equipment (*, equipment:equipment_id(*))
      `)
      .order('start_date', { ascending: false });
    
    if (error) {
      console.error('Ошибка при получении бронирований:', error);
      return { data: [], error };
    }
    
    // Если есть данные, получаем пользователей отдельным запросом
    if (data && data.length > 0) {
      // Собираем уникальные ID пользователей
      const userIds = [...new Set(data.map(booking => booking.user_id))];
      console.log(`Найдено ${data.length} бронирований, запрашиваем информацию о ${userIds.length} пользователях`);
      
      // Получаем данные пользователей отдельным запросом
      const { data: usersData, error: usersError } = await supabaseServiceRole
        .from('users')
        .select('id, username')
        .in('id', userIds);
      
      if (usersError) {
        console.warn('Не удалось получить данные пользователей:', usersError);
      } else if (usersData) {
        // Создаем мапу пользователей для быстрого доступа
        const usersMap = usersData.reduce((map, user) => {
          map[user.id] = user;
          return map;
        }, {} as Record<string, any>);
        
        // Добавляем данные пользователей к бронированиям
        data.forEach(booking => {
          // Используем приведение типов, чтобы избежать ошибок TS
          const bookingAny = booking as any;
          bookingAny.users = usersMap[booking.user_id] || null;
        });
      }
    }
    
    console.log('Успешно получены бронирования:', data ? data.length : 0, 'записей');
    if (data && data.length > 0) {
      console.log('Пример первой записи:', JSON.stringify(data[0], null, 2));
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Непредвиденная ошибка при получении бронирований:', error);
    return { data: [], error };
  }
};

// Обновление статуса бронирования
export const updateBookingStatus = async (id: string, status: 'pending' | 'confirmed' | 'completed' | 'cancelled') => {
  try {
    console.log('Обновление статуса бронирования:', id, 'на', status);
    
    // Сначала проверяем текущий статус бронирования
    const { data: currentBooking, error: checkError } = await supabaseServiceRole
      .from('bookings')
      .select('status')
      .eq('id', id)
      .single();
      
    if (checkError) {
      console.error('Ошибка при получении текущего статуса бронирования:', checkError);
      return { data: null, error: checkError };
    }
    
    // Если текущий статус совпадает с целевым, ничего не делаем
    if (currentBooking.status === status) {
      console.log(`Статус бронирования ${id} уже установлен как ${status}, пропускаем обновление`);
      return { data: currentBooking, error: null };
    }
    
    // Обновляем статус бронирования через RPC функцию
    const { data: updateResult, error: updateError } = await supabaseServiceRole.rpc(
      'update_booking_status',
      { p_booking_id: id, p_status: status }
    );
      
    if (updateError) {
      console.error('Ошибка обновления статуса бронирования:', updateError);
      return { data: null, error: updateError };
    }
    
    console.log('Статус бронирования успешно обновлен, результат:', updateResult);
    
    // Получаем обновленные данные бронирования
    const { data, error } = await supabaseServiceRole
      .from('bookings')
      .select(`
        *,
        booking_equipment (*, equipment:equipment_id(*))
      `)
      .eq('id', id)
      .single();
      
    if (error) {
      console.error('Ошибка получения обновленных данных бронирования:', error);
      return { data: null, error };
    }
    
    console.log('Получены обновленные данные бронирования:', data);
    
    // После успешного обновления статуса, вызываем функцию для обновления сообщения в Telegram
    // через клиентский код, а не через SQL-функцию
    try {
      console.log('Вызов функции editBookingNotification из telegram-notifications.ts...');
      
      // Импортируем функцию из модуля telegram-notifications
      const { editBookingNotification } = await import('./telegram-notifications');
      
      // Вызываем функцию для обновления сообщения
      const telegramUpdateResult = await editBookingNotification(id);
      
      console.log('Результат обновления сообщения в Telegram:', telegramUpdateResult ? 'успешно' : 'ошибка');
    } catch (telegramExc) {
      console.error('Исключение при обновлении сообщения в Telegram:', telegramExc);
    }
    
    return { data, error: null };
  } catch (exc) {
    console.error('Исключение при обновлении статуса бронирования:', exc);
    return { data: null, error: exc as Error };
  }
};

// Получение деталей бронирования по ID
export const getBookingById = async (id: string) => {
  try {
    console.log('Получение информации о бронировании:', id);
    
    // Получаем бронирование без вложенного запроса users
    const { data, error } = await supabaseServiceRole
      .from('bookings')
      .select(`
        *,
        booking_equipment (*, equipment:equipment_id(*))
      `)
      .eq('id', id)
      .single();
      
    if (error) {
      console.error('Ошибка получения информации о бронировании:', error);
      return { data: null, error };
    }
    
    // Если есть данные, получаем информацию о пользователе отдельным запросом
    if (data && data.user_id) {
      const { data: userData, error: userError } = await supabaseServiceRole
        .from('users')
        .select('id, username')
        .eq('id', data.user_id)
        .single();
      
      if (userError) {
        console.warn('Не удалось получить данные пользователя:', userError);
      } else if (userData) {
        // Используем приведение типов, чтобы избежать ошибок TS
        const dataAny = data as any;
        dataAny.users = userData;
      }
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Ошибка при получении информации о бронировании:', error);
    return { data: null, error };
  }
};

// Удаление бронирования
export const deleteBooking = async (id: string) => {
  try {
    console.log('Удаление бронирования:', id);
    
    // Сначала пытаемся удалить уведомление в Telegram, если оно есть
    try {
      await deleteBookingNotification(id);
    } catch (notificationError) {
      console.warn('Не удалось удалить уведомление из Telegram:', notificationError);
      // Продолжаем выполнение, так как удаление уведомления не критично для основной операции
    }
    
    // Сначала получаем информацию о забронированном оборудовании
    const { data: bookingEquipment, error: fetchError } = await supabaseServiceRole
      .from('booking_equipment')
      .select('equipment_id')
      .eq('booking_id', id);
    
    if (fetchError) {
      console.error('Ошибка получения данных о оборудовании бронирования:', fetchError);
      return { success: false, error: fetchError };
    }
    
    // Сохраняем ID оборудования для последующего обновления статуса
    const equipmentIds = bookingEquipment.map(item => item.equipment_id);
    console.log('Оборудование для обновления статуса:', equipmentIds);
    
    // Удаляем связанные записи в booking_equipment
    const { error: itemsError } = await supabaseServiceRole
      .from('booking_equipment')
      .delete()
      .eq('booking_id', id);
      
    if (itemsError) {
      console.error('Ошибка удаления позиций бронирования:', itemsError);
      return { success: false, error: itemsError };
    }
    
    // Затем удаляем саму запись бронирования
    const { error } = await supabaseServiceRole
      .from('bookings')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error('Ошибка удаления бронирования:', error);
      return { success: false, error };
    }
    
    // Обновляем статус оборудования на "available"
    if (equipmentIds.length > 0) {
      const { error: updateError } = await supabaseServiceRole
        .from('equipment')
        .update({ status: 'available' })
        .in('id', equipmentIds);
      
      if (updateError) {
        console.error('Ошибка обновления статуса оборудования:', updateError);
        // Не возвращаем ошибку, так как основная операция (удаление бронирования) выполнена успешно
        console.warn('Бронирование удалено, но статус оборудования не обновлен');
      } else {
        console.log('Статус оборудования успешно обновлен на "available"');
      }
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Ошибка при удалении бронирования:', error);
    return { success: false, error };
  }
};

// Альтернативная функция для получения бронирований, используя прямой SQL запрос
export const getUserBookingsAlternative = async (userId: string) => {
    try {
        console.log('Альтернативное получение бронирований пользователя с ID:', userId);
        
        // Использование подхода с прямым запросом к таблицам
        console.log('Выполняем прямой запрос к таблицам...');
        const { data: directData, error: directError } = await supabaseServiceRole
            .from('bookings')
            .select(`
                *,
                booking_equipment!booking_equipment_booking_id_fkey (
                    *,
                    equipment!booking_equipment_equipment_id_fkey (*)
                )
            `)
            .eq('user_id', userId);
            
        if (directError) {
            console.error('Ошибка при прямом запросе бронирований:', directError);
            return { data: [], error: directError };
        }
        
        console.log('Получены данные прямым запросом:', directData);
        
        // Преобразуем данные в нужный формат
        const formattedData = directData.map(booking => {
            const equipment = booking.booking_equipment.map(item => item.equipment);
            return {
                ...booking,
                equipment
            };
        });
        
        return { data: formattedData, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при альтернативном получении бронирований:', error);
        return { data: [], error };
    }
};

// SQL-функция для создания в Supabase
/*
-- Создание SQL-функции для получения бронирований с оборудованием
CREATE OR REPLACE FUNCTION get_user_bookings_with_equipment(p_user_id UUID)
RETURNS JSONB AS $$
DECLARE
  result JSONB;
BEGIN
  WITH user_bookings AS (
    SELECT b.* 
    FROM bookings b
    WHERE b.user_id = p_user_id
    ORDER BY b.created_at DESC
  ),
  booking_items AS (
    SELECT 
      be.booking_id,
      jsonb_agg(
        jsonb_build_object(
          'id', e.id,
          'name', e.name,
          'category_id', e.category_id,
          'daily_rate', e.daily_rate,
          'description', e.description,
          'image_url', e.image_url,
          'quantity', be.quantity,
          'status', e.status
        )
      ) AS equipment
    FROM booking_equipment be
    JOIN equipment e ON be.equipment_id = e.id
    WHERE be.booking_id IN (SELECT id FROM user_bookings)
    GROUP BY be.booking_id
  )
  SELECT 
    jsonb_agg(
      jsonb_build_object(
        'id', ub.id,
        'user_id', ub.user_id,
        'start_date', ub.start_date,
        'end_date', ub.end_date,
        'status', ub.status,
        'created_at', ub.created_at,
        'updated_at', ub.updated_at,
        'equipment', COALESCE(bi.equipment, '[]'::jsonb)
      )
    ) INTO result
  FROM user_bookings ub
  LEFT JOIN booking_items bi ON ub.id = bi.booking_id;
  
  RETURN COALESCE(result, '[]'::jsonb);
END;
$$ LANGUAGE plpgsql;
*/

// Функция для получения недоступных дат для конкретного оборудования
export const getEquipmentBlockedDates = async (equipmentId: string): Promise<{ data: Date[], error: any }> => {
  try {
    console.log(`Получение недоступных дат для оборудования ID: ${equipmentId}`);
    
    // Устанавливаем начальную дату на сегодня, а конечную - на 6 месяцев вперед
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sixMonthsLater = new Date();
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    sixMonthsLater.setHours(23, 59, 59, 999);
    
    // Форматируем даты для запроса
    const formattedStartIso = today.toISOString();
    const formattedEndIso = sixMonthsLater.toISOString();
    
    // Запрашиваем бронирования для данного оборудования в указанном периоде
    // Исключаем бронирования со статусами 'cancelled' и 'completed'
    const { data, error } = await supabaseServiceRole
      .from('booking_equipment')
      .select(`
        booking_id,
        quantity,
        bookings (
          start_date,
          end_date,
          status
        )
      `)
      .eq('equipment_id', equipmentId)
      .gte('bookings.start_date', formattedStartIso)
      .lte('bookings.end_date', formattedEndIso)
      .not('bookings.status', 'in', '("cancelled","completed")');
    
    if (error) {
      console.error('Ошибка при получении бронирований оборудования:', error);
      return { data: [], error };
    }
    
    // Получаем информацию о доступном количестве оборудования
    const { data: equipmentData, error: equipmentError } = await supabaseServiceRole
      .from('equipment')
      .select('quantity')
      .eq('id', equipmentId)
      .single();
    
    if (equipmentError) {
      console.error('Ошибка при получении информации об оборудовании:', equipmentError);
      return { data: [], error: equipmentError };
    }
    
    // Общее количество единиц оборудования
    const totalQuantity = equipmentData.quantity || 0;
    
    // Создаем карту занятости по датам
    const occupancyMap = new Map<string, number>();
    
    // Заполняем карту данными о бронированиях
    if (data && data.length > 0) {
      data.forEach((bookingItem: any) => {
        // Пропускаем, если нет данных о бронировании
        if (!bookingItem.bookings) return;
        
        // Пропускаем завершенные и отмененные бронирования
        if (bookingItem.bookings.status === 'completed' || bookingItem.bookings.status === 'cancelled') return;
        
        const startDate = new Date(bookingItem.bookings.start_date);
        const endDate = new Date(bookingItem.bookings.end_date);
        const quantity = bookingItem.quantity || 1;
        
        // Перебираем все даты в диапазоне бронирования
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateKey = currentDate.toISOString().split('T')[0]; // Формат YYYY-MM-DD
          
          // Обновляем количество занятых единиц на эту дату
          const currentOccupancy = occupancyMap.get(dateKey) || 0;
          occupancyMap.set(dateKey, currentOccupancy + quantity);
          
          // Переходим к следующей дате
          currentDate.setDate(currentDate.getDate() + 1);
        }
      });
    }
    
    // Определяем, какие даты полностью заблокированы
    const blockedDates: Date[] = [];
    
    // Проверяем каждую дату в периоде
    const currentDate = new Date(today);
    while (currentDate <= sixMonthsLater) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const occupiedCount = occupancyMap.get(dateKey) || 0;
      
      // Если все единицы оборудования заняты на эту дату, добавляем её в список заблокированных
      if (occupiedCount >= totalQuantity) {
        blockedDates.push(new Date(currentDate));
      }
      
      // Переходим к следующей дате
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log(`Найдено ${blockedDates.length} заблокированных дат для оборудования ID ${equipmentId}`);
    return { data: blockedDates, error: null };
  } catch (error) {
    console.error('Ошибка при получении недоступных дат:', error);
    return { data: [], error };
  }
};

// Интерфейс для обновления бронирования
export interface UpdateBookingData {
    booking_id: string;
    start_date?: string;
    end_date?: string;
    total_price?: number;
    guests?: number;
    project_name?: string | null;
    equipment_items?: {
        equipment_id: string;
        quantity: number;
        daily_rate?: number;
    }[];
    equipment_data?: string; // JSON строка с данными об оборудовании
}

// Обновление существующего бронирования
export const updateBooking = async (bookingData: UpdateBookingData) => {
    console.log('Обновление бронирования:', bookingData);
    
    try {
        // Проверяем, существует ли бронирование
        const { data: existingBooking, error: fetchError } = await supabaseServiceRole
            .from('bookings')
            .select('*')
            .eq('id', bookingData.booking_id)
            .single();
            
        if (fetchError || !existingBooking) {
            console.error('Ошибка при получении бронирования:', fetchError);
            return { data: null, error: fetchError || { message: 'Бронирование не найдено' } };
        }
        
        let equipmentItems = bookingData.equipment_items;
        
        // Если equipment_data предоставлены в виде строки JSON, распарсим их
        if (bookingData.equipment_data && !equipmentItems) {
            try {
                equipmentItems = JSON.parse(bookingData.equipment_data);
            } catch (e) {
                console.error('Ошибка при парсинге equipment_data:', e);
                return { error: 'Некорректный формат данных оборудования' };
            }
        }
        
        // Если нет оборудования для обновления, получаем текущее оборудование
        if (!equipmentItems) {
            const { data: currentEquipment, error: equipmentError } = await supabaseServiceRole
                .from('booking_equipment')
                .select('*')
                .eq('booking_id', bookingData.booking_id);
                
            if (equipmentError) {
                console.error('Ошибка при получении текущего оборудования:', equipmentError);
                return { data: null, error: equipmentError };
            }
            
            equipmentItems = currentEquipment.map(item => ({
                equipment_id: item.equipment_id,
                quantity: item.quantity
            }));
        }
        
        // Обновляем данные бронирования
        const updateData: any = {};
        if (bookingData.start_date) updateData.start_date = bookingData.start_date;
        if (bookingData.end_date) updateData.end_date = bookingData.end_date;
        if (bookingData.total_price !== undefined) updateData.total_price = bookingData.total_price;
        if (bookingData.guests) updateData.guests = bookingData.guests;
        if (bookingData.project_name !== undefined) updateData.project_name = bookingData.project_name;
        
        // Вывод данных для отладки
        console.log('Данные для обновления бронирования:', updateData);
        console.log('Оборудование для обновления:', equipmentItems);
        
        // Обновляем бронирование только если есть что обновлять
        if (Object.keys(updateData).length > 0) {
            const { error: updateError } = await supabaseServiceRole
                .from('bookings')
                .update(updateData)
                .eq('id', bookingData.booking_id);
                
            if (updateError) {
                console.error('Ошибка при обновлении бронирования:', updateError);
                return { data: null, error: updateError };
            }
        }
        
        // Обновляем оборудование для бронирования
        if (equipmentItems && equipmentItems.length > 0) {
            // Сначала удаляем существующее оборудование
            const { error: deleteError } = await supabaseServiceRole
                .from('booking_equipment')
                .delete()
                .eq('booking_id', bookingData.booking_id);
                
            if (deleteError) {
                console.error('Ошибка при удалении оборудования:', deleteError);
                return { data: null, error: deleteError };
            }
            
            // Затем добавляем новое оборудование
            const bookingEquipmentData = equipmentItems.map(item => ({
                booking_id: bookingData.booking_id,
                equipment_id: item.equipment_id,
                quantity: item.quantity
            }));
            
            console.log('Данные оборудования для вставки:', bookingEquipmentData);
            
            const { error: insertError } = await supabaseServiceRole
                .from('booking_equipment')
                .insert(bookingEquipmentData);
                
            if (insertError) {
                console.error('Ошибка при добавлении оборудования:', insertError);
                return { data: null, error: insertError };
            }
        }

        // Получаем обновленные данные бронирования
        const { data: updatedBooking, error: fetchUpdatedError } = await supabaseServiceRole
            .from('bookings')
            .select(`
                *,
                booking_equipment (*, equipment:equipment_id(*))
            `)
            .eq('id', bookingData.booking_id)
            .single();
            
        if (fetchUpdatedError) {
            console.error('Ошибка при получении обновленного бронирования:', fetchUpdatedError);
            // Не возвращаем ошибку, так как основная операция уже выполнена
        } else {
            console.log('Обновленное бронирование:', updatedBooking);
        }
        
        // Обновляем сообщение в Telegram
        try {
            const notificationUpdated = await editBookingNotification(bookingData.booking_id);
            console.log(`Обновление уведомления для бронирования ${bookingData.booking_id} ${notificationUpdated ? 'успешно выполнено' : 'не удалось выполнить'}`);
        } catch (notificationError) {
            // Не прерываем процесс обновления бронирования при ошибке обновления уведомления
            console.error('Ошибка при обновлении уведомления в Telegram:', notificationError);
        }
        
        return { data: { booking_id: bookingData.booking_id, success: true }, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при обновлении бронирования:', error);
        return { data: null, error: { message: 'Непредвиденная ошибка при обновлении бронирования' } };
    }
};

// Получение всего оборудования
export const getAllEquipment = async () => {
    try {
        console.log('Получение списка всего оборудования');
        
        // Используем supabaseServiceRole вместо supabase для обхода ограничений доступа
        const { data, error } = await supabaseServiceRole
            .from('equipment')
            .select('*, category:categories(*)')
            .order('sort_order', { ascending: true });
            
        if (error) {
            console.error('Ошибка при получении списка оборудования:', error);
            return { data: null, error };
        }
        
        // Если данные пустые, просто возвращаем пустой массив
        if (!data || data.length === 0) {
            console.log('База данных вернула пустой список оборудования');
            return { data: [], error: null };
        }
        
        console.log(`Получено ${data.length} единиц оборудования из базы данных`);
        return { data, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при получении оборудования:', error);
        return { data: null, error: { message: 'Непредвиденная ошибка при получении оборудования' } };
    }
};

// Получение доступного оборудования на указанные даты
export const getAvailableEquipment = async (startDate: string, endDate: string) => {
    try {
        console.log(`Получение доступного оборудования на период: ${startDate} - ${endDate}`);
        
        // Проверка валидности дат
        if (!startDate || !endDate) {
            console.error('Недопустимый диапазон дат:', { startDate, endDate });
            return getAllEquipment();
        }
        
        // Запрос для получения оборудования с учетом доступности, используем сервисную роль
        const { data, error } = await supabaseServiceRole
            .rpc('check_equipment_availability', {
                p_start_date: startDate,
                p_end_date: endDate
            });
        
        if (error) {
            console.error('Ошибка при получении доступного оборудования:', error);
            
            // Если функция недоступна, выполняем обычный запрос
            console.log('Выполняем запасной запрос для получения всего оборудования...');
            return getAllEquipment();
        }
        
        // Если данные пустые, возвращаем все оборудование
        if (!data || data.length === 0) {
            console.log('Нет доступного оборудования на выбранные даты, возвращаем все оборудование');
            return getAllEquipment();
        }
        
        // Для каждого доступного оборудования получаем информацию о цене из таблицы equipment
        const equipmentIds = data.map((item: any) => item.id);
        
        // Запрашиваем подробную информацию о ценах
        const { data: ratesData, error: ratesError } = await supabaseServiceRole
            .from('equipment')
            .select('id, daily_rate')
            .in('id', equipmentIds);
            
        if (ratesError) {
            console.error('Ошибка при получении информации о ценах:', ratesError);
        } else if (ratesData && ratesData.length > 0) {
            // Создаем карту ID -> daily_rate для быстрого поиска
            const ratesMap: Record<string, number> = ratesData.reduce((map: Record<string, number>, item) => {
                map[item.id] = item.daily_rate;
                return map;
            }, {});
            
            // Обогащаем данные о доступности информацией о ценах
            data.forEach((item: any) => {
                if (ratesMap[item.id]) {
                    item.daily_rate = ratesMap[item.id];
                }
            });
            
            console.log('Данные оборудования обогащены информацией о ценах');
        }
        
        console.log(`Получено ${data.length} единиц доступного оборудования`);
        return { data, error: null };
    } catch (error) {
        console.error('Непредвиденная ошибка при получении доступного оборудования:', error);
        return getAllEquipment(); // При любой ошибке возвращаем все оборудование
    }
};

// Новый тип для скидки пользователя
export interface RoleDiscount {
  id: string;
  role: string;
  discount_percent: number;
  role_display_name?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Получение всех скидок по ролям из представления role_discounts_view
 */
export const getRoleDiscounts = async () => {
  try {
    // Используем as any для обхода проблем с типизацией
    const { data, error } = await (supabaseServiceRole as any)
      .from('role_discounts_view')
      .select('*');
      
    return { data, error };
  } catch (error) {
    console.error('Ошибка при получении скидок:', error);
    return { data: null, error };
  }
};

/**
 * Обновление скидки для определенной роли
 */
export const updateRoleDiscount = async (role: string, discountPercent: number) => {
  try {
    // Используем as any для обхода проблем с типизацией
    const { data, error } = await (supabaseServiceRole as any)
      .rpc('update_role_discount', { 
        p_role: role, 
        p_discount_percent: discountPercent 
      });
      
    return { data, error };
  } catch (error) {
    console.error('Ошибка при обновлении скидки:', error);
    return { data: null, error };
  }
};

/**
 * Получение скидки для текущего пользователя
 * Возвращает процент скидки (0-100)
 */
export const getUserDiscount = async () => {
  try {
    let userId = '';
    let userRole = '';
    
    // Попытка получить ID пользователя из Supabase сессии
    const { data: authUser, error: authError } = await supabase.auth.getSession();
    if (!authError && authUser.session) {
      userId = authUser.session.user.id;
      console.log('🔵 ID пользователя получен из Supabase сессии:', userId);
    } else {
      // Если нет сессии, пробуем получить из localStorage
      const userStr = localStorage.getItem('user') || localStorage.getItem('telegram_user');
      if (userStr) {
        try {
          const parsedUser = JSON.parse(userStr);
          userId = parsedUser.id || parsedUser.user_id;
          userRole = parsedUser.role;
          console.log('🔵 ID пользователя получен из localStorage:', userId, 'роль:', userRole);
        } catch (e) {
          console.error('🔴 Ошибка при парсинге данных пользователя из localStorage:', e);
        }
      }
    }
    
    if (!userId) {
      console.error('🔴 Не удалось получить ID пользователя');
      return { data: 0, error: new Error('Не удалось получить ID пользователя') };
    }

    // Проверка роли напрямую, если получена из localStorage
    if (userRole) {
      console.log('🟍 Проверяем роль напрямую:', userRole);
      // Запрос скидки для роли
      const { data: roleData, error: roleError } = await (supabaseServiceRole as any)
        .from('role_discounts')
        .select('discount_percent')
        .eq('role', userRole)
        .single();
        
      if (!roleError && roleData) {
        console.log('🟢 Получена скидка из таблицы role_discounts по роли:', userRole, 'скидка:', roleData.discount_percent);
        return { data: roleData.discount_percent, error: null };
      } else {
        console.warn('🟡 Не удалось получить скидку по роли напрямую, пробуем через RPC:', roleError);
      }
    }
    
    // Вызываем RPC функцию для получения скидки пользователя
    console.log('🔍 Запрос скидки через RPC функцию для пользователя:', userId);
    // Используем as any для обхода проблем с типизацией
    const { data, error } = await (supabaseServiceRole as any)
      .rpc('get_user_discount', { 
        user_id: userId 
      });
    
    if (error) {
      console.error('🔴 Ошибка при получении скидки пользователя через RPC:', error);
      return { data: 0, error };
    }
    
    console.log('🟢 Получена скидка через RPC для пользователя:', userId, 'скидка:', data);
    
    // Функция возвращает число (процент скидки)
    return { data, error: null };
  } catch (error) {
    console.error('🔴 Непредвиденная ошибка при получении скидки пользователя:', error);
    return { data: 0, error };
  }
};

/**
 * Обновляет статус всех подтвержденных бронирований (confirmed), у которых истек срок
 * Запускается как руками из админки, так и автоматически по расписанию
 */
export const updateExpiredBookingsStatus = async () => {
  try {
    console.log('Запуск автоматического обновления статусов просроченных бронирований');
    
    // Используем прямой SQL-запрос с помощью функции query вместо rpc
    const { data, error } = await supabaseServiceRole
      .from('bookings')
      .update({ status: 'completed', updated_at: new Date().toISOString() })
      .eq('status', 'confirmed')
      .lt('end_date', new Date().toISOString())
      .select('id');
      
    if (error) {
      console.error('Ошибка при обновлении статусов просроченных бронирований:', error);
      return { success: false, count: 0, error };
    }
    
    const count = data ? data.length : 0;
    console.log('Результат обновления статусов просроченных бронирований:', count);
    return { success: true, count, error: null };
  } catch (error) {
    console.error('Исключение при обновлении статусов просроченных бронирований:', error);
    return { success: false, count: 0, error };
  }
};

export default supabase; 